import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import withRouter from "../../../components/withRouter";
import * as Yup from "yup";
import { useFormik } from "formik";
import { authClient } from "../../../lib/auth-client";
import { toast } from "react-toastify";
import { MailCheck, Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { FieldDescription } from "../../../components/ui/field";
import { Input } from "../../../components/ui/input";

const ForgetPasswordPage = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const validation: any = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("E-mail inválido")
        .required("Por favor, insira seu e-mail"),
    }),
    onSubmit: async (values: { email: string }) => {
      setLoader(true);

      try {
        const { error } = await authClient.requestPasswordReset({
          email: values.email,
          redirectTo: "/reset-password",
        });

        if (error) {
          toast.error(error.message || "Erro ao enviar link de redefinição");
        } else {
          toast.success("Link de redefinição de senha enviado com sucesso!");
          navigate("/login");
        }
      } catch {
        toast.error("Erro ao enviar link de redefinição");
      } finally {
        setLoader(false);
      }
    },
  });

  document.title = "Redefinir senha | Ad Pulse";

  return (
    <React.Fragment>
      <div className="relative isolate min-h-screen bg-white text-gray-900">
        {/* Main content */}
        <div className="flex min-h-[calc(100vh-88px)] items-center justify-center px-4 pb-10">
          <Card className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-sm">
            <CardHeader className="space-y-1 pb-4 pt-7 text-center">
              <CardTitle className="text-xl font-bold text-gray-900 sm:text-3xl">
                Esqueceu sua senha?
              </CardTitle>
              <CardDescription className="text-sm text-gray-500">
                Envie seu e-mail para receber um link de redefinição
              </CardDescription>
              <div className="mt-3 flex justify-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
                  <MailCheck className="h-6 w-6" />
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-5 px-4 pb-5 sm:px-7 sm:pb-7">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-center text-xs text-amber-800 sm:text-sm">
                Informe o e-mail cadastrado na sua conta e, caso ele exista em
                nossa base, enviaremos um link de redefinição.
              </div>

              <form
                autoComplete="on"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSubmitted(true);
                  validation.handleSubmit();
                }}
                className="space-y-5"
              >
                {/* Email */}
                <div>
                  <label
                    className="mb-1.5 block text-sm font-medium text-gray-700"
                    htmlFor="email"
                  >
                    E-mail
                  </label>
                  <div className="relative">
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="seu@email.com"
                      autoComplete="email"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.email}
                      className={`h-11 rounded-lg border-slate-200 bg-slate-50 text-sm text-gray-700 placeholder:text-gray-400 ${
                        submitted && validation.errors.email
                          ? "!border-red-500 focus-visible:!ring-red-500"
                          : "focus-visible:ring-red-500/20"
                      }`}
                    />
                  </div>
                  {submitted && validation.errors.email ? (
                    <FieldDescription className="mt-1 flex items-center gap-1 !text-red-600">
                      <span className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold leading-none text-white">
                        !
                      </span>
                      {validation.errors.email}
                    </FieldDescription>
                  ) : null}
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loader}
                  className="mt-2 h-11 w-full rounded-lg border-0 bg-red-600 font-semibold text-white hover:bg-red-700 active:bg-red-800"
                >
                  {loader ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Enviar link de redefinição
                </Button>
              </form>

              <div className="pt-1 text-center">
                <p className="text-xs text-gray-500 sm:text-sm">
                  Lembrou da sua senha?{" "}
                  <Link
                    to="/login"
                    className="font-semibold text-red-600 underline underline-offset-4 hover:text-red-700"
                  >
                    Voltar para o login
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <footer className="pb-4 text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()} Ad Pulse. Feito com{" "}
          <span className="text-red-500">&#10084;</span>
        </footer>
      </div>
    </React.Fragment>
  );
};

export default withRouter(ForgetPasswordPage);
